import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { X, Plus, Minus, ShoppingCart, Trash2, ArrowRight, Truck, Check, LogIn, User } from 'lucide-react';
import { useCart, SHIPPING_OPTIONS, FREE_SHIPPING_THRESHOLD, isFreeShippingEligible } from '../context/CartContext.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import TrustBadges from './TrustBadges';
import CartPromoProgress from './CartPromoProgress';
import { toast } from 'sonner';
import { optimizedImageProps } from '../utils/imageUrl';

const CartSidebar = () => {
  const { cartItems, isCartOpen, setIsCartOpen, updateQuantity, removeFromCart, clearCart, getCartTotal, shippingOption, setShippingOption } = useCart();
  const { user, isAuthenticated, login, signup } = useAuth();
  const navigate = useNavigate();
  const totals = getCartTotal();

  // Auth modal state
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'signup'
  const [authForm, setAuthForm] = useState({ email: '', password: '', name: '', phone: '' });
  const [authLoading, setAuthLoading] = useState(false);

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      if (authMode === 'login') {
        await login(authForm.email, authForm.password);
        toast.success('Welcome back!');
      } else {
        const digits = (authForm.phone || '').replace(/\D/g, '');
        if (digits.length < 10) {
          toast.error('Please enter a valid phone number (10+ digits)');
          setAuthLoading(false); return;
        }
        await signup(authForm.email, authForm.password, authForm.name, 'en', authForm.phone);
        toast.success('Account created! Check your email for the verification code.');
      }
      setShowAuthModal(false);
      setAuthForm({ email: '', password: '', name: '', phone: '' });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleCheckoutClick = () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
    } else {
      setIsCartOpen(false);
      navigate('/checkout');
    }
  };

  if (!isCartOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        onClick={() => setIsCartOpen(false)}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9998
        }}
      />
      
      {/* Cart Sidebar */}
      <div 
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: '100%',
          maxWidth: '420px',
          height: '100vh',
          background: 'var(--bg-primary)',
          boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <div 
          style={{ 
            padding: '16px 20px', 
            borderBottom: '1px solid var(--border-light)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: 'var(--bg-subtle)',
            color: 'white'
          }}
        >
          <div className="flex items-center gap-3">
            <ShoppingCart size={22} />
            <h2 className="heading-3" style={{ fontSize: '18px', margin: 0 }}>Your Cart</h2>
            <span 
              style={{ 
                background: '#00E5FF', 
                color: '#000', 
                borderRadius: '50%',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 'bold'
              }}
            >
              {totals.itemCount}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {cartItems.length > 0 && (
              <button 
                onClick={() => { clearCart(); toast.success('Cart cleared'); }}
                data-testid="clear-cart-btn"
                style={{ 
                  background: 'rgba(255,255,255,0.15)', 
                  border: '1px solid rgba(255,255,255,0.3)', 
                  cursor: 'pointer', 
                  color: 'white',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <Trash2 size={14} />
                Clear
              </button>
            )}
            <button 
              onClick={() => setIsCartOpen(false)}
              data-testid="close-cart-btn"
              style={{ 
                background: 'rgba(255,255,255,0.2)', 
                border: 'none', 
                cursor: 'pointer', 
                color: 'white',
                width: '36px',
                height: '36px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <X size={22} />
            </button>
          </div>
        </div>

        {/* Scrollable Content Area */}
        <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
          {cartItems.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingCart size={48} className="mx-auto mb-4" style={{ color: 'var(--text-light)' }} />
              <p className="heading-3 mb-2">Your cart is empty</p>
              <p className="body-regular mb-6" style={{ color: 'var(--text-secondary)' }}>
                Add products to get started
              </p>
              <button 
                onClick={() => setIsCartOpen(false)}
                className="btn-primary"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Cart Items */}
              {cartItems.map((item) => (
                <div 
                  key={item.id}
                  data-testid={`cart-item-${item.id}`}
                  style={{ 
                    background: 'var(--bg-secondary)', 
                    border: '1px solid var(--border-light)',
                    padding: '12px',
                    borderRadius: '8px'
                  }}
                >
                  <div className="flex gap-3">
                    <div 
                      style={{ 
                        width: '56px', 
                        height: '56px', 
                        background: 'var(--bg-secondary)',
                        border: '1px solid var(--border-light)',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      {item.image ? (
                        <img 
                          {...optimizedImageProps(item.image, { sizes: '64px', maxWidth: 160 })}
                          alt={item.name}
                          width="64" height="64" loading="lazy" decoding="async"
                          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        />
                      ) : (
                        <ShoppingCart size={20} style={{ color: 'var(--text-light)' }} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 style={{ fontSize: '13px', fontWeight: '600', margin: 0, lineHeight: '1.3' }}>{item.name}</h4>
                      <p style={{ fontSize: '11px', color: 'var(--text-light)', margin: '2px 0' }}>
                        {item.dosage} · {item.packLabel}
                      </p>
                      <div className="flex justify-between items-center mt-2">
                        <div className="flex items-center gap-1">
                          <button 
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            data-testid={`qty-minus-${item.id}`}
                            style={{ 
                              width: '26px', height: '26px', 
                              border: '1px solid var(--border-medium)',
                              background: 'var(--bg-secondary)', borderRadius: '4px',
                              cursor: 'pointer', display: 'flex',
                              alignItems: 'center', justifyContent: 'center'
                            }}
                          >
                            <Minus size={12} />
                          </button>
                          <span style={{ width: '30px', textAlign: 'center', fontWeight: '600', fontSize: '13px' }}>
                            {item.quantity}
                          </span>
                          <button 
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            data-testid={`qty-plus-${item.id}`}
                            style={{ 
                              width: '26px', height: '26px', 
                              border: '1px solid var(--border-medium)',
                              background: 'var(--bg-secondary)', borderRadius: '4px',
                              cursor: 'pointer', display: 'flex',
                              alignItems: 'center', justifyContent: 'center'
                            }}
                          >
                            <Plus size={12} />
                          </button>
                        </div>
                        <div className="flex items-center gap-3">
                          <span style={{ color: 'var(--brand-primary)', fontWeight: '700', fontSize: '14px' }}>
                            ${(item.price * item.quantity).toFixed(2)}
                          </span>
                          <button 
                            onClick={() => removeFromCart(item.id)}
                            data-testid={`remove-item-${item.id}`}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: '2px' }}
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Shipping notice — prices are quoted on invoice */}
              <div style={{ background: '#eff6ff', padding: '10px 12px', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                <div className="flex items-start gap-2">
                  <Truck size={14} style={{ color: '#1d4ed8', marginTop: '2px', flexShrink: 0 }} />
                  <span style={{ color: '#1e3a8a', fontWeight: '500', fontSize: '12px', lineHeight: '1.4' }}>
                    Shipping is calculated and confirmed on your invoice after review. Pick your preferred method below.
                  </span>
                </div>
              </div>

              {/* Shipping Options */}
              <div>
                <label style={{ color: 'var(--text-secondary)', fontWeight: '600', display: 'block', marginBottom: '8px', fontSize: '13px' }}>
                  Shipping Method
                </label>
                <div className="space-y-1">
                  {Object.values(SHIPPING_OPTIONS).map((option) => {
                    const { subtotal } = getCartTotal();
                    const effectivePrice = isFreeShippingEligible(option.id, subtotal) ? 0 : option.price;
                    return (
                    <label 
                      key={option.id}
                      data-testid={`cart-shipping-${option.id}-label`}
                      className="flex items-center justify-between cursor-pointer"
                      style={{ 
                        padding: '8px 10px',
                        background: shippingOption === option.id ? 'rgba(0, 229, 255, 0.08)' : 'var(--bg-secondary)',
                        border: `1.5px solid ${shippingOption === option.id ? 'var(--brand-primary)' : 'var(--border-light)'}`,
                        borderRadius: '6px', transition: 'all 0.2s ease'
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="radio" name="shipping" value={option.id}
                          checked={shippingOption === option.id}
                          onChange={(e) => setShippingOption(e.target.value)}
                          data-testid={`cart-shipping-${option.id}-radio`}
                          style={{ accentColor: 'var(--brand-primary)', width: '15px', height: '15px' }}
                        />
                        <div>
                          <span style={{ fontWeight: '500', display: 'block', fontSize: '13px' }}>{option.name}</span>
                          <span style={{ color: 'var(--text-light)', fontSize: '11px' }}>{option.days}</span>
                        </div>
                      </div>
                      <span
                        data-testid={`cart-shipping-${option.id}-price`}
                        style={{
                          fontWeight: 600,
                          color: effectivePrice === 0 ? '#10b981' : 'var(--brand-primary)',
                          fontSize: '13px',
                          whiteSpace: 'nowrap',
                          marginLeft: '8px',
                        }}
                      >
                        {effectivePrice === 0 ? 'FREE' : `$${effectivePrice.toFixed(2)}`}
                      </span>
                    </label>
                    );
                  })}
                </div>
              </div>
              
              {/* Local Pickup Info */}
              {shippingOption === 'pickup' && (
                <div style={{ 
                  background: '#ecfdf5', padding: '12px', borderRadius: '8px',
                  border: '1px solid #10b981'
                }}>
                  <div className="flex items-start gap-2">
                    <Truck size={16} style={{ color: '#059669', marginTop: '2px', flexShrink: 0 }} />
                    <div>
                      <p style={{ color: '#065f46', fontWeight: '600', fontSize: '12px', marginBottom: '4px' }}>Pickup Location</p>
                      <p style={{ color: '#047857', lineHeight: '1.4', fontSize: '12px', margin: 0 }}>
                        346 William Montgomery RD, Livingston, TX 77351
                      </p>
                      <a href="mailto:research@rcmbiosciences.com?subject=Local%20pickup" style={{ color: '#059669', fontWeight: '600', textDecoration: 'none', fontSize: '12px', marginTop: '4px', display: 'inline-block' }} data-testid="cart-pickup-email">
                        Email to arrange pickup
                      </a>
                    </div>
                  </div>
                </div>
              )}

              {/* Diluent-with-peptide cross-sell removed 2026-02 for compliance.
                  Laboratory Research Diluent remains a standalone SKU under
                  Laboratory Supplies but is no longer surfaced as a reminder,
                  bundle, or upsell tied to peptide items in cart. */}
            </div>
          )}
        </div>

        {/* Fixed Bottom: Totals & Checkout */}
        {cartItems.length > 0 && (
          <div style={{ 
            borderTop: '2px solid var(--border-light)', 
            padding: '16px 20px', 
            background: 'var(--bg-secondary)',
            flexShrink: 0
          }}>
            {(() => {
              const selectedOpt = SHIPPING_OPTIONS[shippingOption] || SHIPPING_OPTIONS.priority;
              const shipFee = isFreeShippingEligible(selectedOpt.id, totals.subtotal) ? 0 : (selectedOpt.price || 0);
              const orderTotal = totals.subtotal + shipFee;
              return (
                <>
                  <CartPromoProgress items={cartItems} />
                  <div className="flex justify-between items-center" style={{ marginBottom: '4px' }}>
                    <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Subtotal</span>
                    <span style={{ fontSize: '13px' }}>${totals.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center" style={{ marginBottom: '8px' }} data-testid="cart-summary-shipping">
                    <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                      Shipping
                      <span style={{ color: 'var(--text-light)', fontSize: '11px', display: 'block' }}>
                        {selectedOpt.name}
                      </span>
                    </span>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: shipFee === 0 ? '#10b981' : 'inherit' }}>
                      {shipFee === 0 ? 'FREE' : `$${shipFee.toFixed(2)}`}
                    </span>
                  </div>
                  <div className="flex justify-between items-center" style={{
                    borderTop: '1px solid var(--border-light)',
                    paddingTop: '8px', marginBottom: '12px'
                  }}>
                    <span style={{ fontWeight: '700', fontSize: '16px' }}>Order Total</span>
                    <span data-testid="cart-summary-total" style={{ color: 'var(--brand-primary)', fontWeight: '700', fontSize: '18px' }}>${orderTotal.toFixed(2)}</span>
                  </div>
                </>
              );
            })()}
            
            <TrustBadges variant="compact" />
            
            {isAuthenticated && user && (
              <div style={{ 
                background: 'rgba(0, 229, 255, 0.08)', padding: '8px 12px', borderRadius: '6px',
                marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px'
              }}>
                <User size={15} style={{ color: 'var(--brand-primary)' }} />
                <span style={{ fontWeight: '600', color: 'var(--brand-primary)', fontSize: '13px' }}>
                  {user.name || user.email}
                </span>
              </div>
            )}
            
            <button 
              onClick={handleCheckoutClick}
              data-testid="checkout-btn"
              className="btn-primary w-full flex items-center justify-center gap-2"
              style={{ padding: '14px', fontSize: '15px', fontWeight: '700' }}
            >
              {isAuthenticated ? (
                <>
                  Checkout — ${totals.total.toFixed(2)}
                  <ArrowRight size={18} />
                </>
              ) : (
                <>
                  <LogIn size={18} />
                  Sign In to Checkout
                </>
              )}
            </button>
            
            {!isAuthenticated && (
              <p style={{ color: 'var(--text-secondary)', fontSize: '11px', textAlign: 'center', marginTop: '6px' }}>
                Sign in required to place orders
              </p>
            )}
            {isAuthenticated && (
              <p style={{ color: 'var(--text-light)', fontSize: '11px', textAlign: 'center', marginTop: '6px' }}>
                Pay via Zelle, Cash App, or Apple Cash
              </p>
            )}
          </div>
        )}
        
        {/* Auth Modal */}
        {showAuthModal && (
          <>
            <div 
              onClick={() => setShowAuthModal(false)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.7)',
                zIndex: 10001
              }}
            />
            <div 
              style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                background: 'var(--bg-primary)',
                borderRadius: '12px',
                padding: '32px',
                width: '90%',
                maxWidth: '400px',
                zIndex: 10002,
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
              }}
            >
              <button 
                onClick={() => setShowAuthModal(false)}
                style={{ 
                  position: 'absolute', 
                  top: '16px', 
                  right: '16px', 
                  background: 'none', 
                  border: 'none', 
                  cursor: 'pointer' 
                }}
              >
                <X size={24} style={{ color: 'var(--text-secondary)' }} />
              </button>
              
              <h2 className="heading-2 mb-2" style={{ color: 'var(--brand-primary)' }}>
                {authMode === 'login' ? 'Sign In' : 'Create Account'}
              </h2>
              <p className="body-regular mb-6" style={{ color: 'var(--text-secondary)' }}>
                {authMode === 'login' 
                  ? 'Sign in to complete your order' 
                  : 'Create an account to place your order'}
              </p>
              
              <form onSubmit={handleAuth}>
                {authMode === 'signup' && (
                  <>
                  <div style={{ marginBottom: '16px' }}>
                    <label className="body-small" style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={authForm.name}
                      onChange={(e) => setAuthForm({ ...authForm, name: e.target.value })}
                      required
                      data-testid="auth-name-input"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '1px solid var(--border-medium)',
                        borderRadius: '8px',
                        fontSize: '16px',
                        color: '#0f172a',
                        background: '#ffffff',
                        WebkitTextFillColor: '#0f172a'
                      }}
                      placeholder="John Doe"
                    />
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <label className="body-small" style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={authForm.phone}
                      onChange={(e) => setAuthForm({ ...authForm, phone: e.target.value })}
                      required
                      data-testid="auth-phone-input"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '1px solid var(--border-medium)',
                        borderRadius: '8px',
                        fontSize: '16px',
                        color: '#0f172a',
                        background: '#ffffff',
                        WebkitTextFillColor: '#0f172a'
                      }}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  </>
                )}
                
                <div style={{ marginBottom: '16px' }}>
                  <label className="body-small" style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={authForm.email}
                    onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })}
                    required
                    data-testid="auth-email-input"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid var(--border-medium)',
                      borderRadius: '8px',
                      fontSize: '16px',
                      color: '#0f172a',
                      background: '#ffffff',
                      WebkitTextFillColor: '#0f172a'
                    }}
                    placeholder="you@example.com"
                  />
                </div>
                
                <div style={{ marginBottom: '24px' }}>
                  <label className="body-small" style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>
                    Password
                  </label>
                  <input
                    type="password"
                    value={authForm.password}
                    onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
                    required
                    minLength={6}
                    data-testid="auth-password-input"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid var(--border-medium)',
                      borderRadius: '8px',
                      fontSize: '16px',
                      color: '#0f172a',
                      background: '#ffffff',
                      WebkitTextFillColor: '#0f172a'
                    }}
                    placeholder="••••••••"
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={authLoading}
                  data-testid="auth-submit-btn"
                  className="btn-primary w-full"
                  style={{ padding: '14px', marginBottom: '16px', opacity: authLoading ? 0.7 : 1 }}
                >
                  {authLoading ? 'Please wait...' : (authMode === 'login' ? 'Sign In' : 'Create Account')}
                </button>
              </form>
              
              <p className="body-regular text-center" style={{ color: 'var(--text-secondary)' }}>
                {authMode === 'login' ? (
                  <>
                    Don't have an account?{' '}
                    <button 
                      onClick={() => setAuthMode('signup')}
                      style={{ color: 'var(--brand-primary)', fontWeight: '600', background: 'none', border: 'none', cursor: 'pointer' }}
                    >
                      Sign Up
                    </button>
                  </>
                ) : (
                  <>
                    Already have an account?{' '}
                    <button 
                      onClick={() => setAuthMode('login')}
                      style={{ color: 'var(--brand-primary)', fontWeight: '600', background: 'none', border: 'none', cursor: 'pointer' }}
                    >
                      Sign In
                    </button>
                  </>
                )}
              </p>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default CartSidebar;
